// @ts-strict-ignore
import { Injectable, forwardRef, Inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, flatMap, map, tap } from 'rxjs/operators';
import * as urltemplate from 'url-template';

import { AppConstants, LMFormType } from '@utils/app.constants';
import { LMActionService, LMActionRequest } from '@services/lm-action.service';
import {
  LMFormContext,
  LMPanelTransactionArgs
} from './lm-form-action-util.service';
import { LMLoggerService } from '@services/lm-logger.service';
import {
  LMActionFormDialogResult,
  LMActionFormDialogResultCode,
  LMActionFormDialogService
} from '@modal/lm-action-form-dialog.service';
import { LMSpecService } from '@services/lm-spec.service';
import { LMModel, LMModelMultiPartFields } from '@form/model/lm-model';
import { LMUrlUtilService } from '@services/utils/lm-url-util.service';
import { KeyedResource } from '@resources/keyed.resource';
import { LMNavigationService } from '@navigation/lm-navigation.service';
import { LMTransactionService, LMTransactionState } from '@transaction/index';
import { LMGlobalUtilService } from '@app/utils';
import { LMContextService } from '@app/services';

@Injectable()
export class LMFormTransactionService {
  constructor(
    @Inject(forwardRef(() => LMActionFormDialogService)) private lmActionFormDialogService,
    private lmActionService: LMActionService,
    private lmLoggerService: LMLoggerService,
    private lmNavigationService: LMNavigationService,
    private lmSpecService: LMSpecService,
    private lmTransactionService: LMTransactionService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmGlobalUtilService: LMGlobalUtilService,
    private lmContextService: LMContextService,
  ) {}

  private createActionRedirectUrl(
    formUrlInfo: LMFormUrlInfo,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): string {
    const actionRedirectUrlTemplate = urltemplate.parse(formUrlInfo.actionRedirectUrl);
    const url = actionRedirectUrlTemplate.expand({
      resourceId: dataView.resourceId,
      actionName: actionSpec.name,
      contextResourceId: dataView.resourceId
    });
    return url;
  }

  private getTransactionState(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): LMTransactionState {
    const formSpec = formContext.formSpec;
    const url = this.lmTransactionService.createFormTransactionUrl(formContext, actionSpec, dataView, formContext.sourceResourceId);
    const transaction = new LMTransactionState(url);
    transaction.busFormSpec = formSpec.busFormSpec;
    transaction.multiPartFields = formContext.formModel?.multiPartFields;
    return transaction;
  }

  public runIndependentAction(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    const transaction = this.getTransactionState(formContext, actionSpec, dataView);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ dataView ], actionSpec });
    transaction.setRequestPayload(transactionRequests);

    // this is a special entry into the transaction service! We do NOT want to prompt for an action form.
    return this.lmTransactionService.runCheckResponseRequired(transaction, formContext.formSpec.busClassSpec, formContext.formSpec.busFormSpec).pipe(
      // proceed if response is done or not required; check if confirmation is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckConfirmationRequired(actionSpec, transaction)),
      catchError(this.lmTransactionService.checkCancelled),
      flatMap((proceed: boolean) => proceed ?
        this.lmTransactionService.executeAction(transaction) :
        of(transaction)
      )
    );
  }

  public runValidateFormPanel(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView,
    validatePanelName: string
  ): Observable<LMTransactionState> {
    const transaction = this.getTransactionState(formContext, actionSpec, dataView);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({
      dataViewLists: [ dataView ],
      actionSpec,
      validateOnly: true,
      validatePanelName
    });

    transaction.setRequestPayload(transactionRequests);

    return this.lmTransactionService.executeAction(transaction);
  }

  // TODO: No longer needed?
  public runValidateKeysOnly(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    const url = this.createValidateKeysOnlyTransactionUrl(formContext.formUrlInfo, formContext.formSpec, actionSpec, dataView);
    const transaction = new LMTransactionState(url);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({
      dataViewLists: [ dataView ],
      actionSpec,
      validateOnly: true,
      validatePanelName: '',
      validateKeysOnly: true
    });

    transaction.setRequestPayload(transactionRequests);

    return this.lmTransactionService.executeAction(transaction);
  }

  public runSetAction(formContext: LMFormContext, actionSpec: LMActionSpec, actionInfo: LMActionInfo, dataView: LMDataView): Observable<LMTransactionState> {
    return this.runInstanceAction(formContext, actionSpec, actionInfo, dataView);
  }

  public runInstanceAction(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    actionInfo: LMActionInfo,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    const transaction = this.getTransactionState(formContext, actionSpec, dataView);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ dataView ], actionSpec });
    transaction.setRequestPayload(transactionRequests);
    const actionChecks$ = this.runActionChecks(transaction, formContext, actionSpec, actionInfo, dataView);
    return actionChecks$.pipe(
      flatMap((proceed: boolean) => proceed ? this.lmTransactionService.executeAction(transaction) : of(transaction))
    );
  }

  public getActionInfoForActionSpecRefactor(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView,
    useActionUrl?: string
  ): Observable<LMActionInfo> {
    if (actionSpec.type === AppConstants.ACTION_TYPE_SET && !actionSpec.hasParameters) {
      return of(<LMActionInfo>undefined);
    }
    // PanelAction such as visited or acknowledged or run on panel business class
    // For eg. JobApplication composite form will have a JobApplicationTask as panel.
    // Use action url supplied in that case. formContext.formUrlInfo is that of JobApplication and not JobApplicationTask.
    // Please NOTE:: When useActionUrl is set, formContext will be UNDEFINED
    const actionUrl = (useActionUrl !== undefined) ? useActionUrl : this.createActionRedirectUrl(formContext.formUrlInfo, actionSpec, dataView);
    if (this.lmContextService.isActionSpecRefactorOn()) {
      return this.lmSpecService.getRedirectUrl(actionUrl, formContext?.stepUpActionResult).pipe(
        flatMap((redirectResponse) => {
          // getRedirectUrl response is not typed, unfortunately.

          // It will return redirect url from action controller. It may be mfaUrl, if action is privileged
          // Or if stepUpActionResult is available, it is action result after mfa code is verified. In that case, the redirect url in the stepUpActionResult is the form url to go to.
          if (redirectResponse.hasOwnProperty('status')) {
            const exception = redirectResponse as LMSystemException;
            const knownExceptions = [AppConstants.STATUS_SYSTEM_ERROR, AppConstants.STATUS_UNAUTHORIZED];
            if (knownExceptions.includes(exception.status)) {
              throw exception;
            }
          }

          if (redirectResponse.mfaUrl) {
            this.lmGlobalUtilService.navigate(redirectResponse.mfaUrl);
            // UI will be unloaded to display MFA Challenge. An empty return from here is ok.
            return of(<LMActionInfo>undefined);
          }

          return of(<LMActionInfo>{
            actionName: actionSpec.name,
            hasActionForm: redirectResponse.hasActionForm,
            redirectUrl: redirectResponse.redirectUrl
          });
        })
      );
    }
    return of(<LMActionInfo>undefined);
  }

  private runActionChecks(
    transaction: LMTransactionState,
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    actionInfo: LMActionInfo,
    dataView: LMDataView
  ): Observable<boolean> {
    const formSpec = formContext.formSpec;
    const buildTransactionRequest = (scheduleView: LMDataView) =>
      this.lmActionService.buildActionRequestForDataView(
        dataView,
        actionSpec,
        null,
        null,
        null,
        scheduleView
      );

    // if (this.lmContextService.isActionSpecRefactorOn()) {
    //   let actionInfoRef = undefined;
    //   const assignActionRef = (actionRef: LMActionInfo): LMActionInfo => {
    //     actionInfoRef = actionRef;
    //     return actionRef;
    //   };
    //   const actionUrl = this.createActionRedirectUrl(formContext.formUrlInfo, actionSpec, dataView);
    //   return this.getActionInfoForActionSpecRefactor(formContext, actionSpec, dataView, actionUrl).pipe(
    //     map((actionInfo: LMActionInfo) => assignActionRef(actionInfo)),
    //     flatMap(actionInfo => this.lmTransactionService.getScheduleView(actionSpec, formSpec.busClassSpec.name, actionInfo)),
    //     filter(scheduleView => (scheduleView !== undefined)),
    //     // build action requests payload
    //     map((scheduleView: LMDataView) => buildTransactionRequest(scheduleView)),
    //     map((transactionRequest: LMActionRequest) => {
    //       transaction.setRequestPayload([ transactionRequest ]);
    //       return this.createActionRedirectUrl(formContext.formUrlInfo, actionSpec, dataView);
    //     }),
    //     // check if form input is required
    //     flatMap((actionUrl: string) => this.lmTransactionService.runCheckFormInputRequired(actionUrl, actionSpec, actionInfoRef, transaction, formContext.stepUpActionResult)),
    //     // proceed if form input is done or not required; check if response is required
    //     tap(this.lmTransactionService.isCancelled),
    //     flatMap(() => this.lmTransactionService.runCheckResponseRequired(transaction, formSpec.busClassSpec, formSpec.busFormSpec)),
    //     // proceed if response is done or not required; check if confirmation is required
    //     tap(this.lmTransactionService.isCancelled),
    //     flatMap(() => this.lmTransactionService.runCheckConfirmationRequired(actionSpec, transaction)),
    //     catchError(this.lmTransactionService.checkCancelled)
    //   );
    // }
    //get schedule view details
    return this.lmTransactionService.getScheduleView(actionSpec, formSpec.busClassSpec.name, actionInfo).pipe(
      filter(scheduleView => (scheduleView !== undefined)),
      // build action requests payload
      map((scheduleView: LMDataView) => buildTransactionRequest(scheduleView)),
      // builds action url
      map((transactionRequest: LMActionRequest) => {
        transaction.setRequestPayload([ transactionRequest ]);
        return this.createActionRedirectUrl(formContext.formUrlInfo, actionSpec, dataView);
      }),
      // check if form input is required
      flatMap((actionUrl: string) => this.lmTransactionService.runCheckFormInputRequired(actionUrl, actionSpec, actionInfo, transaction, formContext.stepUpActionResult)),
      // proceed if form input is done or not required; check if response is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckResponseRequired(transaction, formSpec.busClassSpec, formSpec.busFormSpec)),
      // proceed if response is done or not required; check if confirmation is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckConfirmationRequired(actionSpec, transaction)),
      catchError(this.lmTransactionService.checkCancelled)
    );
  }

  // used by panel Visited and Acknowledge action pattern
  public runPanelAction(
    actionSpec: LMActionSpec,
    dataView: LMDataView,
    args: LMPanelTransactionArgs,
    actionInfo?: LMActionInfo
  ): Observable<LMTransactionState> {
    const transaction = new LMTransactionState(args.transactionUrl);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ dataView ], actionSpec });
    transaction.setRequestPayload(transactionRequests);

    const proceed$ = this.lmTransactionService.runCheckFormInputRequired(args.actionRedirectUrl, actionSpec, actionInfo, transaction).pipe(
      // proceed if form input is done or not required; check if response is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckResponseRequired(transaction, args.busClassSpec)),
      // proceed if response is done or not required; check if confirmation is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckConfirmationRequired(actionSpec, transaction)),
      catchError(this.lmTransactionService.checkCancelled)
    );

    return proceed$.pipe(
      flatMap((proceed: boolean) => proceed ? this.lmTransactionService.executeAction(transaction) : of(transaction))
    );
  }

  public runSaveAction(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    const transaction = this.getTransactionState(formContext, actionSpec, dataView);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ dataView ], actionSpec });
    transaction.setRequestPayload(transactionRequests);
    transaction.isMatrixForm = formContext.formSpec.busFormSpec && LMFormType.MATRIX === formContext.formSpec.busFormSpec.formType;

    // This is called from standard save action in toolbar.
    const actionInfo: LMActionInfo = this.lmContextService.isActionSpecRefactorOn() ? { actionName: actionSpec.name, hasActionForm: false } : undefined;
    const actionChecks$ = this.runActionChecks(transaction, formContext, actionSpec, actionInfo, dataView);
    return actionChecks$.pipe(
      flatMap((proceed: boolean) => proceed ? this.lmTransactionService.executeAction(transaction) : of(transaction))
    );
  }

  public runDeleteAction(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    actionInfo: LMActionInfo,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    return this.runInstanceAction(formContext, actionSpec, actionInfo, dataView);
  }

  public runCreateAction(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    actionSpec: LMActionSpec,
    dataView: LMDataView,
    stepUpActionResult?: LMActionResult
  ): Observable<LMActionFormDialogResult | void> {
    let createActionUrl: string;
    let keyedResource: KeyedResource;
    let contextResourceId: string;
    let actionRedirectUrlTemplate;

    // TODO: need drill RESOURCETYPE evaluation here
    switch (formUrlInfo.sourceResource?.resourceType) {
      case AppConstants.RESOURCETYPE_LIST:
        keyedResource = formUrlInfo.sourceResource as KeyedResource;
        const dataViewCreateResource = new KeyedResource({
          resourceId: dataView.resourceId,
          keyValueArray: keyedResource.keyValueArray
        });
        actionRedirectUrlTemplate = urltemplate.parse(formUrlInfo.actionRedirectUrl);
        createActionUrl = actionRedirectUrlTemplate.expand({
          // get decoded contextResourceId as it will be encoded in the expand
          resourceId: dataViewCreateResource.generateResourceId(false),
          actionName: actionSpec.name,
          contextResourceId: dataView.resourceId
        });
        break;

      case AppConstants.RESOURCETYPE_RELATION:
      case AppConstants.RESOURCETYPE_NAVIGATION:
        // Form to form navigation may not have a fromlist resource.
        if (formUrlInfo.fromListResource) {
          keyedResource = new KeyedResource({ resourceId: formUrlInfo.fromListResource.resourceId });
          contextResourceId = keyedResource.resourceId;
          actionRedirectUrlTemplate = urltemplate.parse(formUrlInfo.actionRedirectUrl);
          createActionUrl = actionRedirectUrlTemplate.expand({
            // remove the list name from the resourceId
            resourceId: contextResourceId.substr(0, contextResourceId.lastIndexOf('.')),
            actionName: actionSpec.name
          });

          // remove the form parameter and add the list parameter
          createActionUrl = this.lmUrlUtilService.removeParam(createActionUrl, 'form');
          createActionUrl = this.lmUrlUtilService.appendToQueryString(createActionUrl, `list=${keyedResource.generateResourceId(true)}`);
        } else {
          createActionUrl = this.createActionRedirectUrl(formUrlInfo, actionSpec, dataView);
        }
        break;

      default:
        // Using current dataview resource id in action url instead of busclassSpec name to pass upper level keys.
        createActionUrl = this.createActionRedirectUrl(formUrlInfo, actionSpec, dataView);
    }

    if (this.lmContextService.isActionSpecRefactorOn()) {
      return this.runActionSpecRefactorMethod(formUrlInfo, formSpec, actionSpec, dataView, createActionUrl, stepUpActionResult);
    }
    if (!actionSpec.hasParameters && !actionSpec.hasActionForm) {
      if (stepUpActionResult) {
        this.lmGlobalUtilService.navigateByUrl(stepUpActionResult.redirectUrl);
        return of();
      }
      return of(this.lmNavigationService.runCreateActionNavigation(createActionUrl));
    }

    return this.runFormActionUrl(createActionUrl, actionSpec, stepUpActionResult);
  }

  private runActionSpecRefactorMethod(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    actionSpec: LMActionSpec,
    dataView: LMDataView,
    actionUrl: string,
    stepUpActionResult?: LMActionResult
  ): Observable<LMActionFormDialogResult | void> {
      return this.lmSpecService.getRedirectUrl(actionUrl, stepUpActionResult).pipe(
        flatMap((redirectResponse) => {
        // getRedirectUrl response is not typed, unfortunately.

        // It will return redirect url from action controller. It may be mfaUrl, if action is privileged
        // Or if stepUpActionResult is available, it is action result after mfa code is verified. In that case, the redirect url in the stepUpActionResult is the form url to go to.
        if (redirectResponse.hasOwnProperty('status')) {
          const exception = redirectResponse as LMError;
          const result = { result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OPENERROR, openError: exception } as LMActionFormDialogResult;
          return of(result);
        }

        if(redirectResponse.mfaUrl) {
          this.lmGlobalUtilService.navigate(redirectResponse.mfaUrl);
          // UI will be unloaded to display MFA Challenge. An empty return from here is ok.
          return of();
        }

        if (!actionSpec.hasParameters && !redirectResponse.hasActionForm) {
          if (stepUpActionResult) {
            this.lmGlobalUtilService.navigateByUrl(stepUpActionResult.redirectUrl);
            return of();
          }
          const redirectUrl = (this.lmContextService.inMingleWidget())
            ? redirectResponse.redirectUrl : this.lmUrlUtilService.toRelativeUrl(redirectResponse.redirectUrl, true, true);
          this.lmGlobalUtilService.navigateByUrl(redirectUrl);
          return of();
        }

        return this.runFormActionUrl(redirectResponse.redirectUrl, actionSpec, stepUpActionResult);
     })
    );
  }

  private runFormActionUrl(actionUrl: string, actionSpec: LMActionSpec, stepUpActionResult?: LMActionResult, response?: LMActionFormUrlResponse): Observable<LMActionFormDialogResult> {

    if (this.lmLoggerService.cskLog === 2) {
      this.lmLoggerService.time({
        id: 'TotalActionForm',
        description: 'TOTAL ACTION FORM',
        className: 'LMFormTransactionService',
        timingStart: 'LMFormTransactionService -> runFormActionUrl()',
        timingEnd: 'LMFormContentComponent -> ngAfterViewInit()'
      });
    }
    const observable = (this.lmContextService.isActionSpecRefactorOn()) ? this.lmSpecService.getFormSpecFromDialogActionUrl(actionUrl)
      : this.lmSpecService.getFormSpecFromRedirectActionUrl(actionUrl, stepUpActionResult);

    const response$ = response ? of(response) :
      observable.pipe(
        // eslint-disable-next-line @typescript-eslint/no-shadow
        map((response: LMActionFormUrlResponse | LMSystemException) => {
          // process LMSystemException
          if (response.hasOwnProperty('status')) {
            const exception = response as LMSystemException;
            const knownExceptions = [ AppConstants.STATUS_SYSTEM_ERROR, AppConstants.STATUS_UNAUTHORIZED ];
            if (knownExceptions.includes(exception.status) ) {
              throw exception;
            }
          }

          return response as LMActionFormUrlResponse;
        }),
      );

    let formUrlInfo: LMFormUrlInfo;
    let formSpec: LMFormSpec;
    const lmActionFormDialogService = (this.lmActionFormDialogService as LMActionFormDialogService);

    return response$.pipe(
      // eslint-disable-next-line @typescript-eslint/no-shadow
      flatMap((response: LMActionFormUrlResponse) => {
        formUrlInfo = response.formUrlInfo;
        formSpec = response.formSpec;

        // construct form data url
        const urlCompiledTemplate = urltemplate.parse(formUrlInfo.initialDataUrl);
        const resourceId = response.resourceId;
        const actionName = response.action;
        const formDataUrl = urlCompiledTemplate.expand({ resourceId, actionName });
        // get form dataview
        return lmActionFormDialogService.getActionFormDialogDataView(formDataUrl);
      }),
      flatMap((formDataView: LMDataView) => {
        if (formDataView.status === AppConstants.STATUS_COMPLETED) {
          return lmActionFormDialogService.runActionFormDialog(formUrlInfo, formSpec, actionSpec, formDataView, null, null, true);
        }
        throw formDataView as LMSystemException;
      }),
      catchError((error) => {
        // unexpected error
        if (!error.status) {
          throw error;
        }
        // handle LMSystemException
        const exception = error as LMSystemException;
        this.lmLoggerService.error(exception);

        const result = {
          result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OPENERROR,
          openError: error
        } as LMActionFormDialogResult;
        return of(result);
      })
    );
  }

  public runUpdateAction(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    actionSpec: LMActionSpec,
    dataView: LMDataView,
    stepUpActionResult?: LMActionResult
  ): Observable<LMActionFormDialogResult | void> {
    // -------------------------------------------------------------------------
    // Need actionRedirectUrl to get the LMFormSpec in order for the dialog
    // to show the target form.
    // -------------------------------------------------------------------------
    const actionRedirectUrlTemplate = urltemplate.parse(formUrlInfo.actionRedirectUrl);
    const actionRedirectUrl = actionRedirectUrlTemplate.expand({
      resourceId: dataView.resourceId,
      actionName: actionSpec.name,
      contextResourceId: dataView.resourceId
    });

    if (this.lmContextService.isActionSpecRefactorOn()) {
      return this.runActionSpecRefactorMethod(formUrlInfo, formSpec, actionSpec, dataView, actionRedirectUrl, stepUpActionResult);
    }
    if (!actionSpec.hasParameters && !actionSpec.hasActionForm) {
      if (stepUpActionResult) {
        this.lmGlobalUtilService.navigateByUrl(stepUpActionResult.redirectUrl);
        return of();
      }
      return of(this.lmNavigationService.runNavigation(actionRedirectUrl));
    }

    return this.runFormActionUrl(actionRedirectUrl, actionSpec, stepUpActionResult);
  }

  public runRelatedAction(
    relatedActionUrl: string,
    actionSpec: LMActionSpec,
    actionInfo: LMActionInfo,
    resourceId: string
  ): Observable<LMActionFormDialogResult> {
    const actionName = actionSpec.name;

    const hasActionForm = this.lmContextService.isActionSpecRefactorOn() ? actionInfo?.hasActionForm : actionSpec.hasActionForm;
    if (!actionSpec.hasParameters && !hasActionForm) {
      const result = {
        result: LMActionFormDialogResultCode.ACTION_FORM_DIALOG_OPENERROR,
        openError: { message: 'Related instance actions w/o a parameter form or action form are not yet supported' }
      } as LMActionFormDialogResult;
      return of(result);
    }

    return this.lmSpecService.getFormSpec(relatedActionUrl).pipe(
      flatMap((response: LMFormSpecResponse | LMSystemException) => {
        const formUrlInfo = (response as LMFormSpecResponse).formUrlInfo;
        const formSpec = (response as LMFormSpecResponse).formSpec;
        const urlCompiledTemplate = urltemplate.parse(formUrlInfo.initialDataUrl);
        const dataUrl = urlCompiledTemplate.expand({ resourceId, actionName });

        const actionFormUrlResponse = {
          formUrlInfo,
          formSpec,
          action: actionName,
          resourceId
        } as LMActionFormUrlResponse;
        return this.runFormActionUrl(dataUrl, actionSpec, undefined, actionFormUrlResponse);
      })
    );
  }

  public runButtonRelatedAction(
    relActionUrl: string,
    actionName: string,
    resourceId: string
  ): Observable<any> {
    const urlCompiledTemplate = urltemplate.parse(relActionUrl);
    const relatedActionUrl = urlCompiledTemplate.expand({
      resourceId,
      actionName
    });

    return this.lmSpecService.getRedirectUrl(relatedActionUrl);
  }

  public runCompareAction(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    // transactionURL does NOT use the action name
    const transactionUrlTemplate = urltemplate.parse(formContext.formUrlInfo.transactionUrl);
    let transactionUrl = transactionUrlTemplate.expand({ resourceId: dataView.resourceId });
    const delimiter = transactionUrl.indexOf('?') === -1 ? '?' : '&';
    transactionUrl = transactionUrl + delimiter + this.lmUrlUtilService.objectToQueryString(formContext.formUrlInfo.navigationContext);
    transactionUrl = transactionUrl + '&executionType=_executeCompare';

    const transactionState: LMTransactionState = new LMTransactionState(transactionUrl);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ dataView ], actionSpec });
    transactionState.busFormSpec = formContext.formSpec.busFormSpec;
    transactionState.multiPartFields = formContext.formModel?.multiPartFields;
    transactionState.setRequestPayload(transactionRequests);

    // No response required or confirmation for compare action
    return this.lmTransactionService.executeAction(transactionState);
  }

  public runProcessFlowAction(
    formContext: LMFormContext,
    pfAction: LMProcessFlowAction,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    const navigationContext = formContext.formUrlInfo.navigationContext;
    const busClassSpec = formContext.formSpec.busClassSpec;
    const busFormSpec = formContext.formSpec.busFormSpec;

    // transactionURL does NOT use the action name
    const transactionUrlTemplate = urltemplate.parse(formContext.formUrlInfo.transactionUrl);
    let transactionUrl = transactionUrlTemplate.expand({ resourceId: dataView.resourceId });
    // Add Navigation context and executionType to URL. Navigation context contains the pfi workunit parameters.
    const delimiter = transactionUrl.indexOf('?') === -1 ? '?' : '&';
    transactionUrl += delimiter + this.lmUrlUtilService.objectToQueryString(navigationContext);
    transactionUrl += '&executionType=_processflow';

    const transaction = new LMTransactionState(transactionUrl);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ dataView ], actionSpec, pfAction });
    transaction.busFormSpec = busFormSpec;
    transaction.multiPartFields = formContext.formModel?.multiPartFields;
    transaction.setRequestPayload(transactionRequests);

    const proceed$ = this.lmTransactionService.runCheckResponseRequiredPF(
      transaction,
      pfAction,
      busClassSpec,
      navigationContext,
      busFormSpec
    ).pipe(
      // proceed if response PF is done or not required; check if response is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckResponseRequired(transaction, busClassSpec, busFormSpec)),
      // proceed if response is done or not required; check if confirmation is required
      tap(this.lmTransactionService.isCancelled),
      flatMap(() => this.lmTransactionService.runCheckConfirmationRequired(actionSpec, transaction)),
      catchError(this.lmTransactionService.checkCancelled)
    );

    return proceed$.pipe(
      flatMap((proceed: boolean) => proceed ? this.lmTransactionService.executeAction(transaction) : of(transaction))
    );
  }

  public runSaveTemplateAction(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    fromDataView: LMDataView,
    templateModels: Array<LMModel>,
    templateDataViewSet: LMDataViewSet,
    displayPanelId: string,
    displayWhenIndex?: number,
    subPanelResourceId?: string
  ): Observable<LMTransactionState> {
    this.lmLoggerService.trace([ '@FormTransactionService -> runSaveTemplateAction' ]);

    const compiledTemplate = urltemplate.parse(formUrlInfo.transactionUrl);
    const transactionUrl = compiledTemplate.expand({ resourceId: fromDataView.resourceId });
    const templateMultiPartFields = new Array<LMModelMultiPartFields>();

    for (let i = 0, len = templateModels.length; i < len; i++) {
      const model = templateModels[i];
      const dataView = model.getData();
      templateDataViewSet.data[i].dataView = dataView;
      templateMultiPartFields.push(model.multiPartFields);
    }

    if (!displayWhenIndex) {
      displayWhenIndex = 0;
    }

    const actionRequest = {
      actionSpec: AppConstants.ACTIONSPEC_TEMPLATEFORM_SAVE,
      templateDataViewSet,
      displayWhenIndex,
      subPanelResourceId,
      displayPanelId, // need for attachments
    } as LMActionRequest;

    const transaction = new LMTransactionState(transactionUrl);
    transaction.busFormSpec = formSpec.busFormSpec;
    transaction.setRequestPayload([ actionRequest ]);
    transaction.templateMultiPartFields = templateMultiPartFields;

    return this.lmTransactionService.executeAction(transaction);
  }

  public runMatrixAction(
    matrixActionTarget: LMMatrixActionTarget,
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    columnDataView: LMDataView
  ): Observable<LMTransactionState> {
    const url = this.createMatrixTransactionUrl(matrixActionTarget, formContext.formUrlInfo, actionSpec, columnDataView);
    const transaction = new LMTransactionState(url);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ columnDataView ], actionSpec });
    transaction.setRequestPayload(transactionRequests);

    const actionChecks$ = this.runActionChecks(transaction, formContext, actionSpec, undefined, columnDataView);
    return actionChecks$.pipe(
      flatMap((proceed: boolean) => proceed ? this.lmTransactionService.executeAction(transaction) : of(transaction))
    );
  }

  private createMatrixTransactionUrl(
    matrixActionTarget: LMMatrixActionTarget,
    formUrlInfo: LMFormUrlInfo,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): string {
    const transactionUrlTemplate = urltemplate.parse(matrixActionTarget === 'column' ?
      formUrlInfo.matrixColumnTransactionUrl : formUrlInfo.matrixCellTransactionUrl);

    return transactionUrlTemplate.expand({
      resourceId: dataView.resourceId,
      actionName: actionSpec.name
    });
  }

  private createValidateKeysOnlyTransactionUrl(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    actionSpec: LMActionSpec,
    dataView: LMDataView
  ): string {
    const transactionUrlTemplate = urltemplate.parse(formUrlInfo.transactionUrl);
    return transactionUrlTemplate.expand({
      resourceId: dataView.resourceId,
      actionName: actionSpec.name
    });
  }

  public processStepUpAuthActionResult(
    formContext: LMFormContext,
    actionResult: LMActionResult,
    dataView: LMDataView
  ): Observable<LMTransactionState> {
    const transaction = this.getTransactionState(formContext, actionResult.actionSpec, dataView);
    const transactionRequests = this.lmActionService.buildActionRequestForDataViewList({ dataViewLists: [ dataView ], actionSpec: actionResult.actionSpec });
    transaction.setRequestPayload(transactionRequests);
    transaction.response = [ actionResult ];
    return this.lmTransactionService.processStepUpAuthActionResult(transaction, actionResult, transactionRequests[0]);
  }
}
